import styled, { css, DefaultTheme } from 'styled-components/macro';
import { FieldStatus } from './field.types';

// ─── Size config ──────────────────────────────────────────────────────────────

export const sizeConfig = (theme: DefaultTheme) =>
  ({
    s: {
      height: '40px',
      paddingInline: theme.spacings.s,
      borderRadius: theme.radiuses.xs,
      textVariant: 'body-14',
      fontSize: theme.fonts['body-14'].fontSize,
      lineHeight: theme.fonts['body-14'].lineHeight,
    },
    m: {
      height: '44px',
      paddingInline: theme.spacings.s,
      borderRadius: theme.radiuses.xs,
      textVariant: 'body-16',
      fontSize: theme.fonts['body-16'].fontSize,
      lineHeight: theme.fonts['body-16'].lineHeight,
    },
  }) as const;

// ─── Status helpers ───────────────────────────────────────────────────────────

const statusBorder = (theme: DefaultTheme, status: FieldStatus) =>
  ({
    error: theme.semantic.border.error,
    warning: theme.semantic.border.warning,
    success: theme.semantic.border.success,
    pending: null,
  })[status];

/**
 * Рамка и фон поля. Hover и focus навешиваются только на доступное поле —
 * отсюда data-атрибуты data-disabled и data-readonly.
 */
const fieldBorderCss = (theme: DefaultTheme, status?: FieldStatus) => {
  const { semantic } = theme;
  const border = status ? statusBorder(theme, status) : null;
  const active = '&:not([data-disabled]):not([data-readonly])';

  return css`
    background-color: ${semantic.field.bg};
    border: 1px solid ${border ?? semantic.border.control};

    ${active}:hover {
      border-color: ${border ?? semantic.border.controlHover};
    }

    ${active}:focus-within {
      border-color: ${border ?? semantic.border.focus};
      outline: none;
    }

    &[data-disabled] {
      background-color: ${semantic.field.bgDisabled};
      border-color: ${semantic.border.control};
    }
  `;
};

/**
 * Общий каркас поля: то, что одинаково у однострочного и многострочного.
 * Размеры, паддинги и раскладка остаются за каждым враппером — они разные.
 */
export const fieldChromeCss = (
  theme: DefaultTheme,
  status?: FieldStatus
) => css`
  position: relative;
  display: inline-flex;
  cursor: text;
  color: ${theme.semantic.field.icon};
  transition-property: border-color, box-shadow, background-color;
  transition-duration: ${theme.tokens.animation.duration.base};

  ${fieldBorderCss(theme, status)}

  &[data-disabled] {
    cursor: not-allowed;
    color: ${theme.semantic.text.disabled};
  }
`;

// ─── Char counter ─────────────────────────────────────────────────────────────

export const CharCounter = styled.span<{ $exceeded: boolean }>`
  ${({ theme, $exceeded }) => css`
    flex: none;
    white-space: nowrap;
    user-select: none;
    color: ${$exceeded
      ? theme.semantic.text.error
      : theme.semantic.text.placeholder};
    transition: color ${theme.tokens.animation.duration.base};

    ${theme.fonts['caption-13'].css}
  `}
`;
